import React, { memo, useCallback, useMemo, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'
import Grid from '@material-ui/core/Grid'
import { useParams } from 'react-router-dom'

import styles from './styles'
import { Loading } from 'components'
import {
	EntityCompanySettingsHeader,
	UserAccounts,
	CompanyBusiness,
	Notifications,
} from './components'
import { Jobs, JobApplicants } from 'screens/search/components'
import {
	ICompanySettingsTabs,
	ECompanyActionTypes,
	ECompanySettingsTabs,
} from 'models'
import { StoreValue } from 'store'
import { EnumUserRoles, useCheckUserPermission } from 'utils'
import { useCompanyQuery } from 'graphql/__generated__/graphql'
const useStyles = makeStyles(styles)

const CompanySettingsView: React.FunctionComponent<any> = ({
	storeUserIg,
	companySettingsTab,
	storeDispatch,
	breadcrumbPath,
}) => {
	const classes = useStyles()
	const { t /*i18n*/ } = useTranslation()
	const { companySlug }: { companySlug?: string } = useParams()

	const tabsObj: ICompanySettingsTabs = {
		UserAccounts: t('User accounts'),
		BusinessId: t('Business ID'),
		Notifications: t('Notifications'),
		Jobs: t('Jobs'),
	}
	const { loading, data } = useCompanyQuery({
		variables: {
			slug: '%' + companySlug + '%',
		},
		fetchPolicy: 'cache-first',
	})

	const companyData = useMemo(() => {
		return data ? data?.companyCollection?.edges?.map(x => x.node)[0] : ''
	}, [data])

	const hasCurrentUserManagerPermission: boolean = useCheckUserPermission(
		EnumUserRoles.COMPANY_MANAGER,
		companyData,
	)

	const hasCurrentUserAdminPermission: boolean = useCheckUserPermission(
		EnumUserRoles.COMPANY_ADMIN,
		companyData,
	)

	const hasCurrentUserPublisherPermission: boolean = useCheckUserPermission(
		EnumUserRoles.PUBLISHER,
		companyData,
	)

	const toggleTabIndexTemp = useCallback(
		(key: string) => {
			storeDispatch({
				type: ECompanyActionTypes.setCompanySettingsTab,
				companySettingsTab: key,
			})
		},
		[storeDispatch],
	)

	useEffect(() => {
		if (!companySettingsTab)
			if (storeUserIg || hasCurrentUserAdminPermission) {
				storeDispatch({
					type: ECompanyActionTypes.setCompanySettingsTab,
					companySettingsTab: ECompanySettingsTabs.UserAccounts,
				})
			} else if (hasCurrentUserManagerPermission) {
				storeDispatch({
					type: ECompanyActionTypes.setCompanySettingsTab,
					companySettingsTab: ECompanySettingsTabs.BusinessId,
				})
			} else if (hasCurrentUserPublisherPermission) {
				storeDispatch({
					type: ECompanyActionTypes.setCompanySettingsTab,
					companySettingsTab: ECompanySettingsTabs.Jobs,
				})
			}
	}, [
		storeUserIg,
		hasCurrentUserAdminPermission,
		hasCurrentUserManagerPermission,
		hasCurrentUserPublisherPermission,
		storeDispatch,
		companySettingsTab,
		tabsObj,
	])

	return loading || !companySettingsTab || !companyData ? (
		<Loading size={48} />
	) : (
		<Grid
			className={classes.companySettingsContainer}
			container={true}
			direction="row"
		>
			{companyData && (
				<>
					<EntityCompanySettingsHeader
						currentTabIndex={companySettingsTab}
						id={companyData.id}
						logoPath={companyData.logoPath ?? ''}
						name={companyData.name ?? ''}
						description={companyData.description ?? ''}
						tabsObj={tabsObj}
						toggleTemp={toggleTabIndexTemp}
						hasCurrentUserAdminPermission={
							hasCurrentUserAdminPermission
						}
						hasCurrentUserManagerPermission={
							hasCurrentUserManagerPermission
						}
						hasCurrentUserPublisherPermission={
							hasCurrentUserPublisherPermission
						}
						storeUserIg={storeUserIg}
					/>
					{companySettingsTab === ECompanySettingsTabs.UserAccounts &&
						(storeUserIg || hasCurrentUserAdminPermission) && (
							<UserAccounts companyId={companyData.id} />
						)}
					{(hasCurrentUserManagerPermission || storeUserIg) && (
						<>
							{companySettingsTab ===
								ECompanySettingsTabs.BusinessId && (
								<CompanyBusiness />
							)}
							{companySettingsTab ===
								ECompanySettingsTabs.Notifications && (
								<Notifications />
							)}
						</>
					)}
					{!breadcrumbPath &&
						(hasCurrentUserPublisherPermission || storeUserIg) &&
						companySettingsTab === ECompanySettingsTabs.Jobs && (
							<Jobs />
						)}
					{breadcrumbPath &&
						(hasCurrentUserPublisherPermission || storeUserIg) &&
						companySettingsTab === ECompanySettingsTabs.Jobs && (
							<JobApplicants />
						)}
				</>
			)}
		</Grid>
	)
}

const CompanySettings = memo((props: any) => (
	<StoreValue
		keys={[
			'user.companyRoles|storeUserCompanyRoles',
			'user._ig|storeUserIg',
			'company.companySettingsTab|companySettingsTab',
			'storeDispatch',
			'company.breadcrumbPath.path|breadcrumbPath',
		]}
	>
		<CompanySettingsView {...props} />
	</StoreValue>
))

export default CompanySettings
